import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { MAX_TABLE_ROWS } from '@/main/app.constants';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import {
  DISPLAY_NAME_COLUMN_MATCH_SEGMENT,
  PROPERTY_COLUMN_MATCH_SEGMENT,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import { buildRowDataForAgGrid, groupKeyFromCell } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { ORIGINAL_NAME_KEY } from '@/trendData/trendData.constants';
import { isCanceled } from '@/utilities/http.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { SortModelItem } from 'ag-grid-community';
import type { Filter } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type { GroupBy } from '@/serverSideTable/serverSideTable.constants';
import type {
  ColumnDefinition,
  MaterializedTableOutput,
  ProcessedTableCell,
  TableJoinData,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { AnyProperty } from '@/utilities.types';
import type { Filter as SdkFilter } from '@seeqdev/api/model/Filter';
import type { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { PropertiesForItemUUIDColumn } from '@seeqdev/api/model/models';

export interface MaterializedTableSort {
  columnName: string;
  ascending: boolean;
}

/**
 * Backend virtual column that resolves to `count(*)` per group (see `VirtualColumns.kt`). Reused as the Tree Builder
 * group child count: under row-lift grouping the group-node's own row is excluded, so this counts leaf descendants.
 * It is only fed to AG Grid's `getChildCount` (never rendered as a column).
 */
export const GROUP_CHILD_COUNT_COLUMN = SeeqNames.MaterializedTables.GroupCountColumn;

/** Row-data field the per-group child count is surfaced under for AG Grid's `getChildCount`. */
export const GROUP_CHILD_COUNT_FIELD = '__groupChildCount';

/**
 * Translates AG Grid's sort model into the materialized-table query `sorts`. The grid column id is the column
 * definition `id`, but the query sorts by `columnName`; item (UUID) columns sort by their display property
 * (`{columnName}.properties.{displayProperty}`) — or `{columnName}.display.Name` when Name is shown with hierarchy
 * aliases — rather than the raw item id. Entries whose column can't be resolved (e.g. the grouping auto-column) are
 * dropped.
 */
export const buildSortsFromSortModel = (
  sortModel: SortModelItem[],
  columns: ColumnDefinition[],
  tableJoinData?: TableJoinData[],
): MaterializedTableSort[] => {
  const columnsById = new Map(columns.map((column) => [column.id, column]));
  return sortModel.flatMap((sortItem) => {
    const column = columnsById.get(sortItem.colId);
    if (!column) {
      return [];
    }
    return [
      {
        columnName: resolveQueryColumnName(column, tableJoinData),
        ascending: sortItem.sort === 'asc',
      },
    ];
  });
};

/** True when the grid shows Name with hierarchy aliases (backend COALESCE via `.display.Name`). */
export const usesDisplayNameColumn = (column: ColumnDefinition, tableJoinData?: TableJoinData[]): boolean =>
  column.columnType === ColumnTypeEnum.UUID &&
  column.displayProperty === SeeqNames.Properties.Name &&
  !!tableJoinData?.length;

/**
 * Backend column name(s) for a grid column in materialized-table queries (sort, filter, distinct, etc.).
 * UUID columns with a display property use the property path from {@link buildPropertiesToInclude}, except
 * Name-with-alias-joins which use `{uuid}.display.Name` (COALESCE of join aliases then item Name).
 * `originalName` maps to the real Name property.
 */
export const resolveQueryColumnNames = (column: ColumnDefinition, tableJoinData?: TableJoinData[]): string[] => {
  if (usesDisplayNameColumn(column, tableJoinData)) {
    return [`${column.columnName}${DISPLAY_NAME_COLUMN_MATCH_SEGMENT}${SeeqNames.Properties.Name}`];
  }
  if (column.columnType === ColumnTypeEnum.UUID && column.displayProperty) {
    const propertyName =
      column.displayProperty === ORIGINAL_NAME_KEY ? SeeqNames.Properties.Name : column.displayProperty;
    return [`${column.columnName}${PROPERTY_COLUMN_MATCH_SEGMENT}${propertyName}`];
  }
  return [column.columnName];
};

/** Primary backend column name for a grid column (first of {@link resolveQueryColumnNames}). */
export const resolveQueryColumnName = (column: ColumnDefinition, tableJoinData?: TableJoinData[]): string =>
  resolveQueryColumnNames(column, tableJoinData)[0];

/** Alias for SSRM filter call sites that historically used the filterable-column naming. */
export const resolveFilterableColumnNames = resolveQueryColumnNames;
export const resolveFilterableColumnName = resolveQueryColumnName;

/**
 * Map of AG Grid colId → backend column name(s) for {@link filterModelToTableFilter}.
 * A string array means OR across those columns (multiple hierarchy alias joins).
 */
export const buildColumnIdToNameMap = (
  columns: ColumnDefinition[],
  tableJoinData?: TableJoinData[],
): Record<string, string | string[]> =>
  Object.fromEntries(
    columns.map((column) => {
      const names = resolveQueryColumnNames(column, tableJoinData);
      return [column.id, names.length === 1 ? names[0] : names];
    }),
  );

/** Backend column names that should coerce filter values to booleans in {@link filterModelToTableFilter}. */
export const getBooleanColumnNames = (columns: ColumnDefinition[]): ReadonlySet<string> =>
  new Set(columns.filter((column) => column.columnType === ColumnTypeEnum.BOOLEAN).map((column) => column.columnName));

/**
 * Reduces an AG Grid group-node key to the scalar the materialized-table query expects. AG Grid reports the key as the
 * row-group column's cell value, which for the item table is a processed cell object rather than a scalar. Cell objects
 * are reduced via {@link groupKeyFromCell} (the same reduction the column `keyCreator` uses); scalars (and the null
 * blank-group sentinel already translated by `getGroupByPayloadFromAgGrid`) pass through.
 */
const normalizeGroupKey = (key: unknown): string | null => {
  if (key === null || typeof key !== 'object') {
    return key as string | null;
  }
  return groupKeyFromCell(key as ProcessedTableCell);
};

/**
 * Builds the backend row-lift `groupBy` for the Tree Builder item table so every displayed non-group column is lifted
 * from the group-node item's own row via [SeeqNames.CustomAggregators.LiftParentRow]. This mirrors the CSRM
 * `assetBuilderGroupingAggregationFunc`, which lifts the whole row rather than aggregating leaves.
 *
 * The AG-Grid-derived `groupBy` identifies columns by their column-definition `id`, but the materialized-table query
 * resolves group and value columns by `columnName` (see `findColumnByName`), so ids are translated to names here.
 * Group keys are normalized from cell objects to the scalar the query coerces per group-column type.
 *
 * Every non-group-column is lifted, and so are the *other* group columns (deeper levels not yet drilled into, and
 * higher levels already in the drill path) — only the current level's group column is excluded, since the backend
 * forbids a column being both grouped and a value column. Lifting the other group columns keeps them tied to the
 * group-node item's own row (null when it has none) instead of falling through to the backend's pick-one aggregate,
 * which would surface an arbitrary leaf value in a column the lifted row has no value for.
 */
export const buildRowLiftGroupBy = (groupBy: GroupBy, columns: ColumnDefinition[]): GroupBy => {
  const columnNameById = new Map(columns.map((column) => [column.id, column.columnName]));
  const currentGroupColumnId = groupBy.rowGroupColumnIds[groupBy.groupKeys.length];
  return {
    rowGroupColumnIds: groupBy.rowGroupColumnIds.map((id) => columnNameById.get(id) ?? id),
    groupKeys: groupBy.groupKeys.map(normalizeGroupKey),
    valueColumns: columns
      .filter((column) => column.id !== currentGroupColumnId)
      .map((column) => ({
        columnId: column.columnName,
        aggregationFunction: SeeqNames.CustomAggregators.LiftParentRow,
      })),
  };
};

const hasFormulaRule = (column: ColumnDefinition): boolean =>
  column.rules.some(
    (rule) =>
      rule.rule === SeeqNames.MaterializedTables.Rules.FormulaCreator ||
      rule.rule === SeeqNames.MaterializedTables.Rules.NumericScalarCreator ||
      rule.rule === SeeqNames.MaterializedTables.Rules.StringScalarCreator,
  );

// Per-column property requests for a materialized-table query. Extracted verbatim from the legacy
// `useFetchMaterializedTable` hook so SSRM fetches request the same item properties.
export const buildPropertiesToInclude = (columns: ColumnDefinition[]): PropertiesForItemUUIDColumn[] =>
  columns
    .filter(
      (column) =>
        column.displayProperty !== undefined ||
        (column.additionalProperties !== undefined && column.additionalProperties.length > 0),
    )
    .map((column) => {
      const propertyNames = _.uniq([
        ...(column.displayProperty ? [column.displayProperty] : []),
        ...(column.additionalProperties ?? []),
        ...(hasFormulaRule(column) ? [SeeqNames.Properties.IsModifiedOutsideTable] : []),
      ]);
      return { uuidColumn: column.columnName, propertyNames };
    });

export interface QueryMaterializedTableRowsParams {
  tableId: string;
  columns: string[];
  properties?: PropertiesForItemUUIDColumn[];
  limit?: number;
  cursorKey?: string;
  filter?: Filter;
  sorts?: MaterializedTableSort[];
  groupBy?: GroupBy;
  tablesToJoin?: { tableDefinitionId: string; columnIdsToInclude?: string[] }[];
  cancellationGroup?: string;
  includePublicationProvenance?: boolean;
  /** Backend-computed virtual columns to request, e.g. the group child-count via {@link GROUP_CHILD_COUNT_COLUMN}. */
  virtualColumns?: { vantage: string[] };
}

/**
 * Runs a single materialized-table page query, returning the raw table output for {@link processMaterializedTable}.
 * Like the legacy `getMaterializedTable` wrapper but with cursor pagination and `groupBy` for the SSRM path.
 */
export const queryMaterializedTableRows = async ({
  tableId,
  columns,
  properties,
  limit,
  cursorKey,
  filter,
  sorts,
  groupBy,
  tablesToJoin,
  cancellationGroup,
  includePublicationProvenance,
  virtualColumns,
}: QueryMaterializedTableRowsParams): Promise<MaterializedTableOutput | undefined> => {
  try {
    const { data } = await sqTablesApi.queryTableData(
      {
        limit: limit || MAX_TABLE_ROWS,
        columnsToInclude: columns,
        propertiesToInclude: properties,
        includeOverrides: true,
        ...(includePublicationProvenance && { includePublicationProvenance: true }),
        tablesToJoin,
        sorts: sorts ?? [],
        ...(cursorKey && { cursor: { cursorKey } }),
        // Local Filter → SDK Filter at the API boundary (same cast as getTable).
        filter: filter as SdkFilter | undefined,
        groupBy,
        virtualColumns,
      },
      { tableId },
      cancellationGroup ? { cancellationGroup } : undefined,
    );
    return data as MaterializedTableOutput;
  } catch (e) {
    if (!isCanceled(e)) {
      errorToast({ httpResponseOrError: e });
    }
    return undefined;
  }
};

/**
 * Converts a raw materialized-table page into AG Grid row data keyed by column id, via the shared
 * {@link processMaterializedTable} + {@link buildRowDataForAgGrid} pipeline so SSRM rows match the legacy CSRM load.
 */
export const processTableResultToRowData = (
  output: MaterializedTableOutput | undefined,
  columns: ColumnDefinition[],
  tableType: TableTypeEnum,
  idsToFilterOut?: string[],
): AnyProperty[] => {
  const processed = processMaterializedTable(output, columns);
  if (!processed) {
    return [];
  }
  const columnDefsForRowData = [
    ...columns.map((column) => ({ field: column.id, headerName: column.columnName })),
    // Map the backend count(*) column onto a private field so it doesn't collide with a user column.
    { field: GROUP_CHILD_COUNT_FIELD, headerName: GROUP_CHILD_COUNT_COLUMN },
  ];
  const items = buildRowDataForAgGrid(columnDefsForRowData, processed, tableType, idsToFilterOut) ?? [];
  // The count arrives wrapped as a processed cell; unwrap to the plain number AG Grid's getChildCount expects.
  for (const item of items) {
    const cell = item[GROUP_CHILD_COUNT_FIELD];
    if (cell !== undefined && cell !== null) {
      const rawValue = typeof cell === 'object' ? (cell as { value?: unknown }).value : cell;
      const count = Number(rawValue);
      item[GROUP_CHILD_COUNT_FIELD] = Number.isFinite(count) ? count : undefined;
    }
  }
  return items;
};
